import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { observer } from 'mobx-react-lite';
import LinkIcon from '@mui/icons-material/Link';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useStore } from 'logic';
import { SessionUserPermission } from '@creative-kit/shared';
import { designTokens } from 'theme';
import { fn } from 'firebase-modules';
import Autocomplete from '@mui/material/Autocomplete';
import { getSessionSharingInfo } from 'api/getSessionSharingInfo';

export const ShareSession = observer(() => {
  const store = useStore();
  const sessionsStore = store?.sessionsStore;
  const currentSession = sessionsStore?.currentSession;
  const { isCurrentSessionOwner } = store?.uiPermissions ?? {};
  const uiSnackbarStore = store?.uiSnackbarStore;
  const uiSessionSharingStore = store?.uiSessionSharingStore;
  const uiTeamsStore = store?.uiTeamsStore;
  const { user } = store?.authStore ?? {};

  const [openShare, setOpenShare] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [sharingMode, setSharingMode] = useState<'private' | 'org'>('private');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [privateModeWarningOpen, setPrivateModeWarningOpen] = useState(false);
  const [removeAllDirectShares, setRemoveAllDirectShares] = useState(false);

  // Organization sharing state
  const [orgPermission, setOrgPermission] = useState<SessionUserPermission>(
    SessionUserPermission.VIEW,
  );
  const [shareWithEntireOrg, setShareWithEntireOrg] = useState(true);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);

  // Direct sharing state
  const [selectedUser, setSelectedUser] = useState<{
    userId: string;
    email: string;
    name: string;
  } | null>(null);
  const [directPermission, setDirectPermission] = useState<SessionUserPermission>(
    SessionUserPermission.VIEW,
  );
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<
    Array<{ userId: string; email: string; name: string }>
  >([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);

  // Guest link sharing state
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPermission, setGuestPermission] = useState<SessionUserPermission>(
    SessionUserPermission.VIEW,
  );
  const [guestMessage, setGuestMessage] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const [expirationType, setExpirationType] = useState<'24h' | '7d' | '30d' | 'custom' | 'never'>(
    '24h',
  );
  const [customExpirationDate, setCustomExpirationDate] = useState<string>('');
  const [isSavingChanges, setIsSavingChanges] = useState(false);

  // Guest user editing state (keyed by tokenId)
  const [editingGuestPermission, setEditingGuestPermission] = useState<string | null>(null);
  const [editingGuestExpiration, setEditingGuestExpiration] = useState<string | null>(null);
  const [guestPermissionValues, setGuestPermissionValues] = useState<
    Record<string, SessionUserPermission>
  >({});
  const [guestExpirationValues, setGuestExpirationValues] = useState<Record<string, string>>({});

  // Owner information state (for non-owners viewing the modal)
  const [ownerInfo, setOwnerInfo] = useState<{ name: string; email: string } | null>(null);
  const [isLoadingOwnerInfo, setIsLoadingOwnerInfo] = useState(false);

  // Load owner information for non-owners
  useEffect(() => {
    const loadOwnerInfo = async () => {
      if (!isCurrentSessionOwner && currentSession?.id && openShare) {
        // First, try to use createdBy email directly (available in session data)
        // This avoids the API call and CORS issues
        if (currentSession?.data.createdBy?.email) {
          setOwnerInfo({
            name: currentSession.data.createdBy.email.split('@')[0] || 'Unknown',
            email: currentSession.data.createdBy.email,
          });
          return; // Use fallback immediately, skip API call
        }

        // Only try API call if createdBy email is not available
        setIsLoadingOwnerInfo(true);
        try {
          const ownerData = await getSessionSharingInfo({ sessionId: currentSession.id });
          setOwnerInfo(ownerData.data);
        } catch (error) {
          // API call failed - use fallback if available
          if (currentSession?.data.createdBy?.email) {
            setOwnerInfo({
              name: currentSession.data.createdBy.email.split('@')[0] || 'Unknown',
              email: currentSession.data.createdBy.email,
            });
          } else {
            // No fallback available
            setOwnerInfo({
              name: 'Unknown',
              email: '',
            });
          }
        } finally {
          setIsLoadingOwnerInfo(false);
        }
      } else if (isCurrentSessionOwner) {
        // Clear owner info if user becomes owner
        setOwnerInfo(null);
      }
    };

    loadOwnerInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession?.id, isCurrentSessionOwner, openShare]);

  useEffect(() => {
    // Initialize sharing mode from session data
    // Note: 'direct' mode is deprecated - direct shares work independently via permission documents
    if (currentSession?.data.sharing?.mode) {
      const { mode } = currentSession.data.sharing;
      // Convert 'direct' to 'private' for backward compatibility
      setSharingMode(mode === 'direct' ? 'private' : mode === 'org' ? 'org' : 'private');
      // Initialize org sharing state from session data
      if (mode === 'org') {
        setShareWithEntireOrg(
          !currentSession.data.sharing.teamIds || currentSession.data.sharing.teamIds.length === 0,
        );
        setSelectedTeamIds(currentSession.data.sharing.teamIds || []);
        // Reset orgPermission to VIEW initially - it will be updated from access list if available
        setOrgPermission(SessionUserPermission.VIEW);
      }
    } else {
      // Default to private if no sharing mode is set
      setSharingMode('private');
    }
    // Reset unsaved changes when session data changes
    setHasUnsavedChanges(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession?.data.sharing?.mode, currentSession?.data.sharing?.teamIds]);

  // Load teams when org mode is selected and user has organization
  useEffect(() => {
    if (!uiTeamsStore) return;
    if (sharingMode === 'org' && user?.organizationId && openShare) {
      uiTeamsStore.loadTeams(user.organizationId);
    }
  }, [sharingMode, user?.organizationId, openShare, uiTeamsStore]);

  // Load access list once when dialog opens (not on tab change)
  useEffect(() => {
    if (!uiSessionSharingStore) return;
    if (openShare && currentSession?.id && isCurrentSessionOwner) {
      uiSessionSharingStore.loadAccessList(currentSession.id);
    }
  }, [openShare, currentSession?.id, isCurrentSessionOwner, uiSessionSharingStore]);

  // Initialize orgPermission from access list when available
  // Watch for when access list finishes loading
  useEffect(() => {
    if (!uiSessionSharingStore) return;
    if (
      openShare &&
      currentTab === 0 &&
      currentSession?.id &&
      sharingMode === 'org' &&
      !uiSessionSharingStore.isLoading
    ) {
      const accessList = uiSessionSharingStore.getAccessList(currentSession.id);
      if (accessList && accessList.accessList.length > 0) {
        // Find organization members (not direct shares) and get their permission level
        const orgMembers = accessList.accessList.filter(
          (entry) => entry.accessType === 'organization' || entry.accessType === 'team',
        );

        if (orgMembers.length > 0) {
          // Get the most permissive permission level among org members
          // If any org member has EDIT, use EDIT, otherwise use VIEW
          const hasEditPermission = orgMembers.some(
            (entry) =>
              entry.permission === SessionUserPermission.EDIT || entry.permission === 'edit',
          );
          setOrgPermission(
            hasEditPermission ? SessionUserPermission.EDIT : SessionUserPermission.VIEW,
          );
        }
      }
    }
  }, [
    openShare,
    currentTab,
    currentSession?.id,
    sharingMode,
    uiSessionSharingStore?.isLoading,
    uiSessionSharingStore,
  ]);

  useEffect(() => {
    const handleOpenDialog = () => setOpenShare(true);
    window.addEventListener('openShareDialog', handleOpenDialog);
    return () => window.removeEventListener('openShareDialog', handleOpenDialog);
  }, []);

  const closeShare = () => {
    setOpenShare(false);
    // Reset guest link form when dialog closes
    setGuestEmail('');
    setGuestMessage('');
    setGuestPermission(SessionUserPermission.VIEW);
    setExpirationType('24h');
    setCustomExpirationDate('');
    // Don't reset org sharing state here - let it be re-initialized from session data
    // This prevents flickering when closing the dialog
    // Reset direct sharing state
    setSelectedUser(null);
    setDirectPermission(SessionUserPermission.VIEW);
    setUserSearchQuery('');
    setUserSearchResults([]);
    // Reset unsaved changes flag
    setHasUnsavedChanges(false);
  };

  const handleSendGuestLink = async () => {
    if (!guestEmail || !currentSession) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestEmail)) {
      uiSnackbarStore.setSnackbarDetails({
        description: 'Please enter a valid email address',
      });
      return;
    }

    // Calculate expiration date based on type
    let expiresAt: Date | null = null;
    let expirationTypeParam: 'custom' | 'never' | undefined;

    if (expirationType === 'never') {
      expiresAt = null;
      expirationTypeParam = 'never';
    } else if (expirationType === 'custom') {
      if (!customExpirationDate) {
        uiSnackbarStore.setSnackbarDetails({
          description: 'Please select a custom expiration date',
        });
        return;
      }
      expiresAt = new Date(customExpirationDate);
      expirationTypeParam = 'custom';
    } else {
      // Predefined durations
      expiresAt = new Date();
      if (expirationType === '24h') {
        expiresAt.setHours(expiresAt.getHours() + 24);
      } else if (expirationType === '7d') {
        expiresAt.setDate(expiresAt.getDate() + 7);
      } else if (expirationType === '30d') {
        expiresAt.setDate(expiresAt.getDate() + 30);
      }
      // Set to end of day for predefined durations
      expiresAt.setHours(23, 59, 59, 999);
    }

    setSendingLink(true);
    try {
      const sendLinkFn = fn.httpsCallable('sendSessionShareLink');
      await sendLinkFn({
        sessionId: currentSession.id,
        email: guestEmail,
        permission: guestPermission,
        message: guestMessage || undefined,
        expiresAt: expiresAt ? expiresAt.toISOString() : null,
        expirationType: expirationTypeParam,
      });

      uiSnackbarStore.setSnackbarDetails({
        description: 'Share link sent successfully',
      });

      // Reload access list to show the new guest user
      if (currentSession?.id) {
        await uiSessionSharingStore.loadAccessList(currentSession.id);
      }

      // Reset form
      setGuestEmail('');
      setGuestMessage('');
      setExpirationType('24h');
      setCustomExpirationDate('');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send share link';
      uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
    } finally {
      setSendingLink(false);
    }
  };

  // Guard: store can be incomplete during route transitions (e.g. watchCurrentSession)
  if (!sessionsStore || !uiSnackbarStore || !uiSessionSharingStore || !uiTeamsStore) {
    return null;
  }

  if (!currentSession) {
    return null;
  }

  /**
   * If not session owner, show an indicator for user to know they're viewing someone else's session
   * This ensures view-only users (direct shares, org shares with view permission) see the simplified modal
   * instead of the full modal with tabs
   */
  if (!isCurrentSessionOwner) {
    return (
      <>
        <Tooltip title="View Share Settings">
          <IconButton size="small" color="inherit" onClick={() => setOpenShare(true)}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Dialog
          open={openShare}
          maxWidth="sm"
          fullWidth
          onClose={closeShare}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(2px)',
            },
          }}
          sx={{
            '& .MuiDialog-paper': {
              backgroundColor: 'rgba(40, 40, 40, 1)',
              border: '2px solid rgba(255, 255, 255, 1)',
            },
          }}
        >
          <DialogTitle>Share Settings</DialogTitle>
          <DialogContent>
            {/* Access level message */}
            {currentSession && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[800]
                      : theme.palette.grey[100],
                  border: '1px solid',
                  borderColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[700]
                      : theme.palette.grey[300],
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {currentSession.canEditSession ? (
                    <GroupIcon fontSize="small" color="action" />
                  ) : (
                    <VisibilityIcon fontSize="small" color="action" />
                  )}
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: designTokens.typography.fontWeight.bold }}
                  >
                    {currentSession.canEditSession ? 'Edit Access' : 'View-Only Access'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {currentSession.canEditSession
                    ? 'You have edit access to this project. Only the project owner can modify sharing settings.'
                    : 'You have view-only access to this project. Only the project owner can modify sharing settings.'}
                </Typography>
              </Box>
            )}

            {/* Project Information Section */}
            <Box sx={{ mt: 3 }}>
              <Accordion
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[800]
                      : theme.palette.grey[100],
                  borderRadius: (theme) => `${theme.shape.borderRadius}px !important`,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    borderRadius: (theme) => `${theme.shape.borderRadius}px !important`,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="project-info-content"
                  id="project-info-header"
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                    },
                    borderTopLeftRadius: (theme) => theme.shape.borderRadius,
                    borderTopRightRadius: (theme) => theme.shape.borderRadius,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: designTokens.typography.fontWeight.bold }}
                  >
                    More Information
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 2, pb: 1 }}>
                  {/* Your Role */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{
                        fontSize: designTokens.typography.fontSize.xs,
                        fontWeight: designTokens.typography.fontWeight.bold,
                        letterSpacing: '0.5px',
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      Your Role
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: designTokens.typography.fontSize.base,
                        fontWeight: designTokens.typography.fontWeight.bold,
                        color: 'text.primary',
                      }}
                    >
                      {currentSession.canEditSession
                        ? 'Editor'
                        : currentSession.canViewSession
                        ? 'Viewer'
                        : 'No Access'}
                    </Typography>
                  </Box>

                  {/* Project Owner */}
                  <Box>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{
                        fontSize: designTokens.typography.fontSize.xs,
                        fontWeight: designTokens.typography.fontWeight.bold,
                        letterSpacing: '0.5px',
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      Project Owner
                    </Typography>
                    {isLoadingOwnerInfo ? (
                      <CircularProgress size={16} />
                    ) : ownerInfo ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: designTokens.typography.fontSize.base,
                              fontWeight: designTokens.typography.fontWeight.bold,
                              color: 'text.primary',
                            }}
                          >
                            {ownerInfo.name}
                          </Typography>
                        </Box>
                        {ownerInfo.email ? (
                          <Box sx={{ pl: 4 }}>
                            <Typography
                              component="a"
                              href={`mailto:${ownerInfo.email}?subject=Question about ${
                                currentSession?.data.name || 'this project'
                              }`}
                              variant="body2"
                              color="primary"
                              sx={{
                                fontSize: designTokens.typography.fontSize.sm,
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <EmailIcon fontSize="small" />
                              {ownerInfo.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                            Email not available
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Owner information unavailable
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeShare}>Close</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
  return (
    <>
      <Tooltip title="View Share Settings">
        <IconButton size="small" color="inherit" onClick={() => setOpenShare(true)}>
          <LinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={openShare}
        maxWidth="sm"
        fullWidth
        onClose={closeShare}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(2px)',
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'rgba(40, 40, 40, 1)',
            border: '2px solid rgba(255, 255, 255, 1)',
          },
        }}
      >
        <DialogTitle>Share Settings</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => setCurrentTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label="General Access" />
            <Tab label="Direct Share" icon={<PersonIcon />} iconPosition="start" />
            <Tab label="Guest Links" icon={<EmailIcon />} iconPosition="start" />
          </Tabs>

          <Box sx={{ px: 3, py: 2 }}>
            {currentTab === 0 && (
              <>
                {/* Sharing Mode Selector */}
                <Box mt={2}>
                  <FormControl component="fieldset" fullWidth>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: designTokens.typography.fontWeight.bold,
                        mb: 1.5,
                      }}
                    >
                      Sharing Mode
                    </Typography>
                    <RadioGroup
                      value={sharingMode}
                      onChange={(e) => {
                        const newMode = e.target.value as typeof sharingMode;
                        setSharingMode(newMode);
                        // Track if this is different from current session state
                        const currentMode = currentSession?.data.sharing?.mode || 'private';
                        // Also reset org-specific state when switching modes
                        if (newMode === 'private') {
                          setShareWithEntireOrg(true);
                          setSelectedTeamIds([]);
                        }
                        setHasUnsavedChanges(newMode !== currentMode);
                      }}
                    >
                      <FormControlLabel
                        value="private"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LockIcon fontSize="small" />
                            <Box>
                              <Typography variant="body2">Private</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Only you have access
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="org"
                        control={<Radio />}
                        disabled={!user?.organizationId}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GroupIcon fontSize="small" />
                            <Box>
                              <Typography variant="body2">Organization</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Share with your organization
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </RadioGroup>
                    {!user?.organizationId && sharingMode === 'org' && (
                      <FormHelperText sx={{ color: 'warning.main', mt: 0.5 }}>
                        You must be part of an organization to use organization sharing
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Mode-specific content */}
                {sharingMode === 'private' && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      This project is private. Only you can view and edit it.
                    </Typography>
                  </Box>
                )}

                {sharingMode === 'org' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {!user?.organizationId ? (
                      <Typography variant="body2" color="error">
                        You must be part of an organization to use organization sharing.
                      </Typography>
                    ) : (
                      <>
                        {/* Permission Level */}
                        <FormControl fullWidth>
                          <InputLabel id="org-permission-label">Permission Level</InputLabel>
                          <Select
                            id="org-permission"
                            labelId="org-permission-label"
                            label="Permission Level"
                            value={orgPermission}
                            onChange={(e) => {
                              setOrgPermission(e.target.value as SessionUserPermission);
                              setHasUnsavedChanges(true);
                            }}
                          >
                            <MenuItem value={SessionUserPermission.VIEW}>View Only</MenuItem>
                            <MenuItem value={SessionUserPermission.EDIT}>Edit</MenuItem>
                          </Select>
                          <FormHelperText>
                            {orgPermission === SessionUserPermission.VIEW
                              ? 'Organization members can view the project but cannot make changes'
                              : 'Organization members can view and edit the project'}
                          </FormHelperText>
                        </FormControl>

                        {/* Share with entire organization */}
                        <FormControl component="fieldset">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={shareWithEntireOrg}
                                onChange={(e) => {
                                  setShareWithEntireOrg(e.target.checked);
                                  if (e.target.checked) {
                                    setSelectedTeamIds([]);
                                  }
                                  setHasUnsavedChanges(true);
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">
                                  Share with entire organization
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  All members of your organization will have access
                                </Typography>
                              </Box>
                            }
                          />
                        </FormControl>

                        {/* Team selector (only if not sharing with entire org) */}
                        {!shareWithEntireOrg && (
                          <FormControl fullWidth>
                            <InputLabel id="team-select-label">Select Teams</InputLabel>
                            <Select
                              id="team-select"
                              labelId="team-select-label"
                              label="Select Teams"
                              multiple
                              value={selectedTeamIds}
                              onChange={(e) => {
                                const { value } = e.target;
                                setSelectedTeamIds(
                                  typeof value === 'string' ? value.split(',') : value,
                                );
                                setHasUnsavedChanges(true);
                              }}
                              renderValue={(selected) => {
                                if (selected.length === 0) {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      No teams selected
                                    </Typography>
                                  );
                                }
                                const teams = uiTeamsStore.getTeamsByOrganization(
                                  user.organizationId || '',
                                );
                                const selectedTeams = teams.filter((team) =>
                                  (selected as string[]).includes(team.id),
                                );
                                return (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selectedTeams.map((team) => (
                                      <Chip key={team.id} label={team.name} size="small" />
                                    ))}
                                  </Box>
                                );
                              }}
                            >
                              {uiTeamsStore
                                .getTeamsByOrganization(user.organizationId || '')
                                .map((team) => (
                                  <MenuItem key={team.id} value={team.id}>
                                    <Checkbox checked={selectedTeamIds.indexOf(team.id) > -1} />
                                    <ListItemText primary={team.name} />
                                  </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                              Select one or more teams to share with. You can select multiple teams
                              using the checkboxes.
                            </FormHelperText>
                          </FormControl>
                        )}
                      </>
                    )}
                  </Box>
                )}

                {/* Direct sharing is handled via the DIRECT SHARE tab, not via sharing mode */}
                {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
                {false && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Share this project with specific Creative Queue users. They will see it in
                      their shared projects.
                    </Typography>

                    {/* User Search/Select */}
                    <FormControl fullWidth>
                      <Autocomplete
                        options={userSearchResults}
                        getOptionLabel={(option) => option.email || option.name || ''}
                        value={selectedUser}
                        onChange={(_event, newValue) => {
                          setSelectedUser(newValue);
                        }}
                        inputValue={userSearchQuery}
                        onInputChange={async (_event, newInputValue) => {
                          setUserSearchQuery(newInputValue);
                          // Only search when a valid email format is entered (provides confidence the email is typed correctly)
                          const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                            newInputValue.trim(),
                          );
                          if (isValidEmail) {
                            // Search users by email via Cloud Function (secure, doesn't require client read access to /users)
                            setIsSearchingUsers(true);
                            try {
                              const searchUsersFn = fn.httpsCallable('searchUsersByEmailPrefix');
                              const result = await searchUsersFn({ query: newInputValue });
                              const { results } = result.data as {
                                results: Array<{ userId: string; email: string; name: string }>;
                              };
                              setUserSearchResults(results);
                            } catch (error) {
                              console.error('Error searching users:', error);
                              setUserSearchResults([]);
                            } finally {
                              setIsSearchingUsers(false);
                            }
                          } else {
                            setUserSearchResults([]);
                          }
                        }}
                        loading={isSearchingUsers}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search Users by Email"
                            placeholder="Enter email address..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isSearchingUsers ? <CircularProgress size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box>
                              <Typography variant="body2">{option.name || 'No name'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.email}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      />
                      <FormHelperText>
                        Search for users by email address. They must have a Creative Queue account.
                      </FormHelperText>
                    </FormControl>

                    {/* Permission Level */}
                    <FormControl fullWidth>
                      <InputLabel id="direct-permission-label">Permission Level</InputLabel>
                      <Select
                        id="direct-permission"
                        labelId="direct-permission-label"
                        label="Permission Level"
                        value={directPermission}
                        onChange={(e) =>
                          setDirectPermission(e.target.value as SessionUserPermission)
                        }
                      >
                        <MenuItem value={SessionUserPermission.VIEW}>View Only</MenuItem>
                        <MenuItem value={SessionUserPermission.EDIT}>Edit</MenuItem>
                      </Select>
                      <FormHelperText>
                        {directPermission === SessionUserPermission.VIEW
                          ? 'User can view the project but cannot make changes'
                          : 'User can view and edit the project'}
                      </FormHelperText>
                    </FormControl>

                    {/* Share button */}
                    <Button
                      variant="contained"
                      onClick={async () => {
                        if (!currentSession || !selectedUser) return;

                        try {
                          await uiSessionSharingStore.shareWithUser(
                            currentSession.id,
                            selectedUser.userId,
                            directPermission,
                          );
                          // Reset after successful share
                          setSelectedUser(null);
                          setUserSearchQuery('');
                          setUserSearchResults([]);
                        } catch (error) {
                          // Error handling is done in the store
                        }
                      }}
                      disabled={!selectedUser}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Share with User
                    </Button>
                  </Box>
                )}

                {/* Access List Section */}
                {isCurrentSessionOwner && (
                  <Box sx={{ mt: 3 }}>
                    <Accordion
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? theme.palette.grey[800]
                            : theme.palette.grey[100],
                        borderRadius: (theme) => `${theme.shape.borderRadius}px !important`,
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          borderRadius: (theme) => `${theme.shape.borderRadius}px !important`,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="access-list-content"
                        id="access-list-header"
                        sx={{
                          '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                          },
                          borderTopLeftRadius: (theme) => theme.shape.borderRadius,
                          borderTopRightRadius: (theme) => theme.shape.borderRadius,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: designTokens.typography.fontWeight.bold }}
                        >
                          Who Has Access
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 2, pb: 1 }}>
                        {uiSessionSharingStore.isLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          (() => {
                            const accessList = uiSessionSharingStore.getAccessList(
                              currentSession?.id || '',
                            );
                            if (!accessList) {
                              return (
                                <Typography variant="body2" color="text.secondary">
                                  Loading access information...
                                </Typography>
                              );
                            }

                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Owner */}
                                <Box>
                                  <Box
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                                  >
                                    <PersonIcon fontSize="small" color="primary" />
                                    <Typography
                                      variant="overline"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: designTokens.typography.fontSize.xs,
                                        fontWeight: designTokens.typography.fontWeight.bold,
                                        letterSpacing: '0.5px',
                                      }}
                                    >
                                      Owner
                                    </Typography>
                                  </Box>
                                  <Box sx={{ pl: 4 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {accessList.owner.name || 'Unknown'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {accessList.owner.email || 'No email'}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Sharing Mode Info */}
                                {accessList.sharingMode && accessList.sharingMode !== 'private' && (
                                  <Box>
                                    <Box
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                                    >
                                      {accessList.sharingMode === 'org' ? (
                                        <BusinessIcon fontSize="small" color="action" />
                                      ) : (
                                        <LinkIcon fontSize="small" color="action" />
                                      )}
                                      <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        sx={{
                                          fontSize: designTokens.typography.fontSize.xs,
                                          fontWeight: designTokens.typography.fontWeight.bold,
                                          letterSpacing: '0.5px',
                                        }}
                                      >
                                        {accessList.sharingMode === 'org'
                                          ? 'Organization Sharing'
                                          : 'Direct Sharing'}
                                      </Typography>
                                    </Box>
                                    {accessList.organizationName && (
                                      <Box sx={{ pl: 4 }}>
                                        <Typography variant="body2">
                                          {accessList.organizationName}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                )}

                                {/* Access List */}
                                {accessList.accessList.length > 0 && (
                                  <Box>
                                    <Box
                                      sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                                    >
                                      <GroupIcon fontSize="small" color="action" />
                                      <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        sx={{
                                          fontSize: designTokens.typography.fontSize.xs,
                                          fontWeight: designTokens.typography.fontWeight.bold,
                                          letterSpacing: '0.5px',
                                        }}
                                      >
                                        People with Access ({accessList.accessList.length})
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        pl: 4,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                      }}
                                    >
                                      {accessList.accessList.map((entry, index) => (
                                        <Box key={`${entry.userId}-${entry.accessType}-${index}`}>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'flex-start',
                                              justifyContent: 'space-between',
                                              gap: 2,
                                            }}
                                          >
                                            <Box sx={{ flex: 1 }}>
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 1,
                                                  mb: 0.5,
                                                }}
                                              >
                                                <Typography
                                                  variant="body2"
                                                  sx={{ fontWeight: 'medium' }}
                                                >
                                                  {entry.name || 'Unknown'}
                                                </Typography>
                                                <Chip
                                                  label={
                                                    entry.accessType === 'organization'
                                                      ? 'Organization'
                                                      : entry.accessType === 'team'
                                                      ? entry.teamName || 'Team'
                                                      : entry.accessType === 'direct'
                                                      ? 'Direct'
                                                      : entry.accessType === 'guest'
                                                      ? 'Guest'
                                                      : entry.accessType
                                                  }
                                                  size="small"
                                                  variant="outlined"
                                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                                />
                                                <Chip
                                                  label={
                                                    entry.permission === SessionUserPermission.VIEW
                                                      ? 'View'
                                                      : entry.permission ===
                                                        SessionUserPermission.EDIT
                                                      ? 'Edit'
                                                      : entry.permission
                                                  }
                                                  size="small"
                                                  color={
                                                    entry.permission === SessionUserPermission.EDIT
                                                      ? 'primary'
                                                      : 'default'
                                                  }
                                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                                />
                                              </Box>
                                              <Typography variant="caption" color="text.secondary">
                                                {entry.email || 'No email'}
                                              </Typography>
                                              {entry.expiresAt && (
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    mt: 0.5,
                                                  }}
                                                >
                                                  <AccessTimeIcon sx={{ fontSize: 12 }} />
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    Expires:{' '}
                                                    {new Date(entry.expiresAt).toLocaleDateString()}
                                                  </Typography>
                                                </Box>
                                              )}
                                              {entry.grantedBy && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{ mt: 0.5, display: 'block' }}
                                                >
                                                  Granted by: {entry.grantedBy.email || 'Unknown'}
                                                </Typography>
                                              )}
                                            </Box>
                                          </Box>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {accessList.accessList.length === 0 &&
                                  accessList.sharingMode === 'private' && (
                                    <Typography variant="body2" color="text.secondary">
                                      Only the owner has access to this project.
                                    </Typography>
                                  )}
                              </Box>
                            );
                          })()
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </>
            )}

            {currentTab === 1 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: designTokens.typography.fontWeight.bold,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <PersonIcon fontSize="small" />
                  Direct Share
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Share this project with specific Creative Queue users. They will see it in their
                  shared projects. Direct shares work independently of your sharing mode (private or
                  organization).
                </Typography>

                {/* User Search/Select */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Autocomplete
                    options={userSearchResults}
                    getOptionLabel={(option) => option.email || option.name || ''}
                    isOptionEqualToValue={(option, value) => option.userId === value?.userId}
                    value={selectedUser}
                    onChange={(_event, newValue) => {
                      setSelectedUser(newValue);
                    }}
                    inputValue={userSearchQuery}
                    onInputChange={async (_event, newInputValue) => {
                      setUserSearchQuery(newInputValue);
                      // Only search when a valid email format is entered (provides confidence the email is typed correctly)
                      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newInputValue.trim());
                      if (isValidEmail) {
                        // Search users by email via Cloud Function (secure, doesn't require client read access to /users)
                        setIsSearchingUsers(true);
                        try {
                          const searchUsersFn = fn.httpsCallable('searchUsersByEmailPrefix');
                          const result = await searchUsersFn({ query: newInputValue });
                          const { results } = result.data as {
                            results: Array<{ userId: string; email: string; name: string }>;
                          };
                          setUserSearchResults(results);
                        } catch (error) {
                          console.error('Error searching users:', error);
                          setUserSearchResults([]);
                        } finally {
                          setIsSearchingUsers(false);
                        }
                      } else {
                        setUserSearchResults([]);
                      }
                    }}
                    loading={isSearchingUsers}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search users by email"
                        placeholder="user@example.com"
                        variant="outlined"
                      />
                    )}
                  />
                </FormControl>

                {/* Permission Level */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="direct-permission-label">Permission Level</InputLabel>
                  <Select
                    id="direct-permission"
                    labelId="direct-permission-label"
                    label="Permission Level"
                    value={directPermission}
                    onChange={(e) => setDirectPermission(e.target.value as SessionUserPermission)}
                  >
                    <MenuItem value={SessionUserPermission.VIEW}>View Only</MenuItem>
                    <MenuItem value={SessionUserPermission.EDIT}>Edit</MenuItem>
                  </Select>
                  <FormHelperText>
                    {directPermission === SessionUserPermission.VIEW
                      ? 'User can view the project but cannot make changes'
                      : 'User can view and edit the project'}
                  </FormHelperText>
                </FormControl>

                {/* Share button */}
                <Button
                  variant="contained"
                  onClick={async () => {
                    if (!currentSession || !selectedUser) return;

                    try {
                      await uiSessionSharingStore.shareWithUser(
                        currentSession.id,
                        selectedUser.userId,
                        directPermission,
                      );
                      // Reset form after a brief delay to allow React to finish rendering
                      // This prevents MUI Autocomplete errors when clearing state
                      // Clear input value first, then selected user, then results
                      setTimeout(() => {
                        setUserSearchQuery('');
                        setSelectedUser(null);
                        setUserSearchResults([]);
                      }, 0);
                    } catch (error) {
                      // Error handling is done in the store
                    }
                  }}
                  disabled={!selectedUser || uiSessionSharingStore.isSharing}
                  sx={{ alignSelf: 'flex-start', mb: 3 }}
                  startIcon={
                    uiSessionSharingStore.isSharing ? <CircularProgress size={16} /> : null
                  }
                >
                  {uiSessionSharingStore.isSharing ? 'Sharing...' : 'Share with User'}
                </Button>

                {/* List of users with direct share access */}
                {(() => {
                  const accessList = uiSessionSharingStore.getAccessList(currentSession?.id || '');
                  const directShareUsers =
                    accessList?.accessList.filter((entry) => entry.accessType === 'direct') || [];

                  if (uiSessionSharingStore.isLoading && !accessList) {
                    return (
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={24} />
                      </Box>
                    );
                  }

                  if (directShareUsers.length === 0) {
                    return (
                      <Box
                        sx={{
                          mt: 3,
                          p: 2,
                          borderRadius: 1,
                          backgroundColor: (theme) => theme.palette.grey[900],
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No users have been directly shared with this project.
                        </Typography>
                      </Box>
                    );
                  }

                  return (
                    <Box sx={{ mt: 3 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: designTokens.typography.fontWeight.bold,
                          mb: 2,
                        }}
                      >
                        Users with Direct Access ({directShareUsers.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {directShareUsers.map((entry) => (
                          <Box
                            key={entry.userId}
                            sx={{
                              p: 2,
                              borderRadius: 1,
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? theme.palette.grey[800]
                                  : theme.palette.grey[100],
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 2,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {entry.name || entry.email || 'Unknown User'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {entry.email}
                              </Typography>
                              {entry.grantedAt && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', mt: 0.5 }}
                                >
                                  Shared {new Date(entry.grantedAt).toLocaleDateString()}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {/* Permission selector */}
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value={entry.permission}
                                  onChange={async (e) => {
                                    if (!currentSession) return;
                                    const newPermission = e.target.value as SessionUserPermission;
                                    try {
                                      await uiSessionSharingStore.updateUserPermission(
                                        currentSession.id,
                                        entry.userId,
                                        newPermission,
                                      );
                                    } catch (error) {
                                      // Error handling is done in the store
                                    }
                                  }}
                                  disabled={uiSessionSharingStore.isSharing}
                                >
                                  <MenuItem value={SessionUserPermission.VIEW}>View Only</MenuItem>
                                  <MenuItem value={SessionUserPermission.EDIT}>Edit</MenuItem>
                                </Select>
                              </FormControl>
                              {/* Remove button */}
                              <Tooltip title="Remove access">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={async () => {
                                      if (!currentSession) return;
                                      try {
                                        await uiSessionSharingStore.revokeUserAccess(
                                          currentSession.id,
                                          entry.userId,
                                        );
                                      } catch (error) {
                                        // Error handling is done in the store
                                      }
                                    }}
                                    disabled={uiSessionSharingStore.isSharing}
                                    color="error"
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            )}

            {currentTab === 2 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: designTokens.typography.fontWeight.bold,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <EmailIcon fontSize="small" />
                  Share with External Collaborators
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Send a secure link via email to share this project with someone who doesn't have a
                  Creative Queue account.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Email Address"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    disabled={sendingLink}
                    placeholder="collaborator@example.com"
                  />

                  <FormControl fullWidth>
                    <InputLabel id="guest-permission-label">Permission Level</InputLabel>
                    <Select
                      id="guest-permission"
                      labelId="guest-permission-label"
                      label="Permission Level"
                      value={guestPermission}
                      onChange={(e) => setGuestPermission(e.target.value as SessionUserPermission)}
                      disabled={sendingLink}
                    >
                      <MenuItem value={SessionUserPermission.VIEW}>View Only</MenuItem>
                      <MenuItem value={SessionUserPermission.EDIT}>Edit</MenuItem>
                    </Select>
                    <FormHelperText>
                      {guestPermission === SessionUserPermission.VIEW
                        ? 'Recipient can view the project but cannot make changes'
                        : 'Recipient can view and edit the project'}
                    </FormHelperText>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel id="expiration-label">Link Expiration</InputLabel>
                    <Select
                      id="expiration"
                      labelId="expiration-label"
                      label="Link Expiration"
                      value={expirationType}
                      onChange={(e) => setExpirationType(e.target.value as typeof expirationType)}
                      disabled={sendingLink}
                    >
                      <MenuItem value="24h">24 hours</MenuItem>
                      <MenuItem value="7d">7 days</MenuItem>
                      <MenuItem value="30d">30 days</MenuItem>
                      <MenuItem value="custom">Custom date</MenuItem>
                      <MenuItem value="never">Never expires</MenuItem>
                    </Select>
                    <FormHelperText>
                      {expirationType === 'never'
                        ? 'The link will never expire'
                        : expirationType === 'custom'
                        ? 'Select a custom expiration date below'
                        : `The link will expire in ${
                            expirationType === '24h'
                              ? '24 hours'
                              : expirationType === '7d'
                              ? '7 days'
                              : '30 days'
                          }`}
                    </FormHelperText>
                  </FormControl>

                  {expirationType === 'custom' && (
                    <TextField
                      label="Expiration Date"
                      type="datetime-local"
                      fullWidth
                      variant="outlined"
                      value={customExpirationDate}
                      onChange={(e) => setCustomExpirationDate(e.target.value)}
                      disabled={sendingLink}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        min: new Date().toISOString().slice(0, 16), // Prevent past dates
                      }}
                    />
                  )}

                  <TextField
                    label="Optional Message"
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    value={guestMessage}
                    onChange={(e) => setGuestMessage(e.target.value)}
                    disabled={sendingLink}
                    placeholder="Add a personal message to include with the share link..."
                  />

                  <Button
                    variant="contained"
                    startIcon={sendingLink ? undefined : <SendIcon />}
                    onClick={handleSendGuestLink}
                    disabled={!guestEmail || sendingLink}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {sendingLink ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        Sending...
                      </Box>
                    ) : (
                      'Send Share Link'
                    )}
                  </Button>
                </Box>

                {/* Guest Users with Access */}
                {(() => {
                  const accessList = uiSessionSharingStore.getAccessList(currentSession.id);
                  const guestUsers =
                    accessList?.accessList.filter((entry) => entry.accessType === 'guest') || [];

                  if (guestUsers.length === 0) {
                    return null;
                  }

                  return (
                    <Box sx={{ mt: 4 }}>
                      <Divider sx={{ mb: 3 }} />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: designTokens.typography.fontWeight.bold,
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <PersonIcon fontSize="small" />
                        Guest Users with Access ({guestUsers.length})
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {guestUsers.map((guest) => {
                          const tokenId = guest.tokenId || guest.userId;
                          const isEditingPermission = editingGuestPermission === tokenId;
                          const isEditingExpiration = editingGuestExpiration === tokenId;
                          const localPermission =
                            guestPermissionValues[tokenId] ||
                            (guest.permission as SessionUserPermission);
                          const localExpiration =
                            guestExpirationValues[tokenId] ||
                            (guest.expiresAt
                              ? new Date(guest.expiresAt).toISOString().slice(0, 16)
                              : '');

                          return (
                            <Box
                              key={tokenId}
                              sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: (theme) =>
                                  theme.palette.mode === 'dark'
                                    ? theme.palette.grey[700]
                                    : theme.palette.grey[300],
                                borderRadius: 1,
                                backgroundColor: (theme) =>
                                  theme.palette.mode === 'dark'
                                    ? theme.palette.grey[800]
                                    : theme.palette.grey[50],
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  gap: 2,
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      mb: 1,
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {guest.email || 'Guest User'}
                                    </Typography>
                                    <Chip
                                      label="Guest"
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                  </Box>

                                  {/* Permission Level */}
                                  <Box sx={{ mb: 1.5 }}>
                                    {isEditingPermission ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Select
                                          size="small"
                                          value={localPermission}
                                          onChange={(e) =>
                                            setGuestPermissionValues({
                                              ...guestPermissionValues,
                                              [tokenId]: e.target.value as SessionUserPermission,
                                            })
                                          }
                                          sx={{ minWidth: 120 }}
                                        >
                                          <MenuItem value={SessionUserPermission.VIEW}>
                                            View Only
                                          </MenuItem>
                                          <MenuItem value={SessionUserPermission.EDIT}>
                                            Edit
                                          </MenuItem>
                                        </Select>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={async () => {
                                            if (!guest.tokenId) return;
                                            try {
                                              await uiSessionSharingStore.updateGuestShareToken(
                                                currentSession.id,
                                                guest.tokenId,
                                                { permission: localPermission },
                                              );
                                              setEditingGuestPermission(null);
                                            } catch (error) {
                                              // Error handled in store
                                            }
                                          }}
                                          disabled={uiSessionSharingStore.isSharing}
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setEditingGuestPermission(null);
                                            setGuestPermissionValues({
                                              ...guestPermissionValues,
                                              [tokenId]: guest.permission as SessionUserPermission,
                                            });
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </Box>
                                    ) : (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                          label={
                                            guest.permission === SessionUserPermission.VIEW
                                              ? 'View Only'
                                              : guest.permission === SessionUserPermission.EDIT
                                              ? 'Edit'
                                              : guest.permission
                                          }
                                          size="small"
                                          color={
                                            guest.permission === SessionUserPermission.EDIT
                                              ? 'primary'
                                              : 'default'
                                          }
                                          sx={{ height: 22, fontSize: '0.7rem' }}
                                        />
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setEditingGuestPermission(tokenId);
                                            setGuestPermissionValues({
                                              ...guestPermissionValues,
                                              [tokenId]: guest.permission as SessionUserPermission,
                                            });
                                          }}
                                          disabled={uiSessionSharingStore.isSharing}
                                        >
                                          Change
                                        </Button>
                                      </Box>
                                    )}
                                  </Box>

                                  {/* Expiration Date */}
                                  <Box sx={{ mb: 1 }}>
                                    {isEditingExpiration ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                          size="small"
                                          type="datetime-local"
                                          value={localExpiration}
                                          onChange={(e) =>
                                            setGuestExpirationValues({
                                              ...guestExpirationValues,
                                              [tokenId]: e.target.value,
                                            })
                                          }
                                          InputLabelProps={{ shrink: true }}
                                          inputProps={{
                                            min: new Date().toISOString().slice(0, 16),
                                          }}
                                          sx={{ minWidth: 200 }}
                                        />
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={async () => {
                                            if (!guest.tokenId) return;
                                            try {
                                              const expiresAt = localExpiration
                                                ? new Date(localExpiration)
                                                : null;
                                              await uiSessionSharingStore.updateGuestShareToken(
                                                currentSession.id,
                                                guest.tokenId,
                                                { expiresAt },
                                              );
                                              setEditingGuestExpiration(null);
                                            } catch (error) {
                                              // Error handled in store
                                            }
                                          }}
                                          disabled={
                                            uiSessionSharingStore.isSharing || !localExpiration
                                          }
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setEditingGuestExpiration(null);
                                            setGuestExpirationValues({
                                              ...guestExpirationValues,
                                              [tokenId]: guest.expiresAt
                                                ? new Date(guest.expiresAt)
                                                    .toISOString()
                                                    .slice(0, 16)
                                                : '',
                                            });
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </Box>
                                    ) : (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AccessTimeIcon sx={{ fontSize: 16 }} />
                                        <Typography variant="caption" color="text.secondary">
                                          {guest.expiresAt
                                            ? `Expires: ${new Date(
                                                guest.expiresAt,
                                              ).toLocaleString()}`
                                            : 'Never expires'}
                                        </Typography>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setEditingGuestExpiration(tokenId);
                                            setGuestExpirationValues({
                                              ...guestExpirationValues,
                                              [tokenId]: guest.expiresAt
                                                ? new Date(guest.expiresAt)
                                                    .toISOString()
                                                    .slice(0, 16)
                                                : '',
                                            });
                                          }}
                                          disabled={uiSessionSharingStore.isSharing}
                                        >
                                          Change
                                        </Button>
                                      </Box>
                                    )}
                                  </Box>

                                  {guest.grantedAt && (
                                    <Typography variant="caption" color="text.secondary">
                                      Shared: {new Date(guest.grantedAt).toLocaleDateString()}
                                    </Typography>
                                  )}
                                </Box>

                                {/* Remove Access Button */}
                                <Tooltip title="Remove access">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={async () => {
                                        if (!guest.tokenId) return;
                                        try {
                                          await uiSessionSharingStore.updateGuestShareToken(
                                            currentSession.id,
                                            guest.tokenId,
                                            { expireNow: true },
                                          );
                                        } catch (error) {
                                          // Error handled in store
                                        }
                                      }}
                                      disabled={uiSessionSharingStore.isSharing}
                                      color="error"
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShare}>Cancel</Button>
          {currentTab === 0 && hasUnsavedChanges && (
            <Button
              onClick={async () => {
                if (!currentSession || isSavingChanges) return;

                setIsSavingChanges(true);
                try {
                  if (sharingMode === 'private') {
                    // Check if switching to private mode with active direct shares
                    const accessList = uiSessionSharingStore.getAccessList(currentSession.id);
                    const directShares =
                      accessList?.accessList.filter((entry) => entry.accessType === 'direct') || [];

                    if (directShares.length > 0) {
                      // Show confirmation modal before saving
                      setPrivateModeWarningOpen(true);
                      setRemoveAllDirectShares(false); // Reset checkbox
                      setIsSavingChanges(false); // Reset saving state since we're not saving yet
                      return; // Don't save yet, wait for user confirmation
                    }

                    // Proceed with save (no direct shares)
                    await uiSessionSharingStore.updateSharingMode(currentSession.id, 'private');
                    setHasUnsavedChanges(false);
                    uiSnackbarStore.setSnackbarDetails({
                      description: 'Project set to private',
                    });
                  } else if (sharingMode === 'org' && user?.organizationId) {
                    if (shareWithEntireOrg) {
                      // Share with entire organization
                      await uiSessionSharingStore.shareWithOrg(
                        currentSession.id,
                        user.organizationId,
                        orgPermission,
                      );
                      // Also update sharing mode configuration
                      await uiSessionSharingStore.updateSharingMode(currentSession.id, 'org', {
                        organizationId: user.organizationId,
                        teamIds: [],
                      });
                    } else if (selectedTeamIds.length > 0) {
                      // Share with specific teams - aggregate results
                      const teams = uiTeamsStore.getTeamsByOrganization(user.organizationId);
                      const selectedTeams = teams.filter((team) =>
                        selectedTeamIds.includes(team.id),
                      );

                      const teamResults: Array<{ teamName: string; grantedCount: number }> = [];

                      for (const teamId of selectedTeamIds) {
                        const team = selectedTeams.find((t) => t.id === teamId);
                        try {
                          const result = await uiSessionSharingStore.shareWithTeam(
                            currentSession.id,
                            user.organizationId,
                            teamId,
                            orgPermission,
                          );
                          // Extract granted count from result if available
                          if (result && team) {
                            const grantedCount = result.grantedCount ?? 0;
                            teamResults.push({ teamName: team.name, grantedCount });
                          } else if (team) {
                            // If no result returned, assume 0 (shouldn't happen but defensive)
                            teamResults.push({ teamName: team.name, grantedCount: 0 });
                          }
                        } catch (error) {
                          // Error handling is done in the store, but continue with other teams
                          console.error(`Failed to share with team ${team?.name}:`, error);
                          if (team) {
                            teamResults.push({ teamName: team.name, grantedCount: 0 });
                          }
                        }
                      }

                      // Update sharing mode configuration with selected teams
                      await uiSessionSharingStore.updateSharingMode(currentSession.id, 'org', {
                        organizationId: user.organizationId,
                        teamIds: selectedTeamIds,
                      });

                      // Show aggregated success message
                      if (selectedTeamIds.length === 1) {
                        // Single team - show team name only
                        const result = teamResults[0];
                        uiSnackbarStore.setSnackbarDetails({
                          description: `Shared with "${result.teamName}"`,
                        });
                      } else {
                        // Multiple teams - show team names
                        const teamSummary = teamResults.map((r) => `"${r.teamName}"`).join(', ');
                        uiSnackbarStore.setSnackbarDetails({
                          description: `Shared with ${teamResults.length} team${
                            teamResults.length === 1 ? '' : 's'
                          }: ${teamSummary}`,
                        });
                      }
                    } else {
                      // Update sharing mode only (no teams selected, not sharing with entire org)
                      await uiSessionSharingStore.updateSharingMode(currentSession.id, 'org', {
                        organizationId: user.organizationId,
                        teamIds: [],
                      });
                    }
                    setHasUnsavedChanges(false);
                  }
                } catch (error) {
                  // Error handling is done in the store
                } finally {
                  setIsSavingChanges(false);
                }
              }}
            >
              {isSavingChanges ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for private mode with active direct shares */}
      <Dialog
        open={privateModeWarningOpen}
        onClose={() => {
          setPrivateModeWarningOpen(false);
          setRemoveAllDirectShares(false);
        }}
      >
        <DialogTitle>Active Direct Shares</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This project currently has active direct shares with other users.
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to set this project to private. Direct shares work independently of your
            sharing mode, so existing direct shares will continue to work unless you remove them.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={removeAllDirectShares}
                onChange={(e) => setRemoveAllDirectShares(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Remove all direct shares when making this project private
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPrivateModeWarningOpen(false);
              setRemoveAllDirectShares(false);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!currentSession) return;

              setPrivateModeWarningOpen(false);
              setIsSavingChanges(true);

              try {
                // Set to private mode
                await uiSessionSharingStore.updateSharingMode(currentSession.id, 'private');

                // If user chose to remove all direct shares, revoke them
                if (removeAllDirectShares) {
                  const accessList = uiSessionSharingStore.getAccessList(currentSession.id);
                  const directShares =
                    accessList?.accessList.filter((entry) => entry.accessType === 'direct') || [];

                  // Revoke all direct shares
                  for (const share of directShares) {
                    try {
                      await uiSessionSharingStore.revokeUserAccess(currentSession.id, share.userId);
                    } catch (error) {
                      console.error(`Failed to revoke access for ${share.email}:`, error);
                      // Continue with other shares even if one fails
                    }
                  }

                  uiSnackbarStore.setSnackbarDetails({
                    description: `Project set to private and ${directShares.length} direct share${
                      directShares.length === 1 ? '' : 's'
                    } removed`,
                  });
                } else {
                  uiSnackbarStore.setSnackbarDetails({
                    description: 'Project set to private. Direct shares remain active.',
                  });
                }

                setHasUnsavedChanges(false);
              } catch (error) {
                // Error handling is done in the store
              } finally {
                setIsSavingChanges(false);
                setRemoveAllDirectShares(false);
              }
            }}
            variant="contained"
            color="primary"
          >
            Make Private
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
